import cn from 'classnames'
import { KeyboardEvent, memo, useCallback } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'

import { IStepProps } from './models'

import styles from './Step.module.scss'

/**
 * Created by plexideas on 02.05.23
 */
export const Step = memo<IStepProps>(
  ({
    label = '',
    description,
    step,
    status,
    onClick,
    direction,
    variant,
    stepIndex,
    color,
    size,
    highlightCompletedStep = false,
    trackAfter,
  }) => {
    const isCompact = variant === 'compact'
    const isStandard = variant === 'standard'
    const isIndicator = variant === 'indicator'
    const isVertical = direction === 'vertical'
    const isClickable = Boolean(onClick) && status !== 'disabled' && status !== 'current'
    const isCurrent = status === 'current'

    const handleOnClick = useCallback(() => {
      if (isClickable && onClick) {
        onClick(stepIndex)
      }
    }, [isClickable, onClick, stepIndex])

    const handleOnKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (!isClickable) {
          return
        }

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onClick?.(stepIndex)
        }
      },
      [isClickable, onClick, stepIndex]
    )

    const interactiveProps = isClickable
      ? {
          role: 'button' as const,
          tabIndex: 0,
          onClick: handleOnClick,
          onKeyDown: handleOnKeyDown,
          'aria-disabled': false,
        }
      : {
          'aria-disabled': true,
        }

    const indicator = (
      <>
        {isCompact && (
          <div
            className={cn(styles.compact, styles[status], { [styles.interactive]: isClickable })}
            aria-current={isCurrent ? 'step' : undefined}
            {...interactiveProps}
          />
        )}
        {isStandard && (
          <div
            className={cn(styles.step, styles[status], { [styles.interactive]: isClickable })}
            aria-current={isCurrent ? 'step' : undefined}
            {...interactiveProps}
          >
            {status === 'complete' ? <i className="kbase kbase-check" /> : step}
          </div>
        )}
        {isIndicator && (
          <div
            className={cn(styles.indicator, styles[status], { [styles.interactive]: isClickable })}
            aria-current={isCurrent ? 'step' : undefined}
            {...interactiveProps}
          />
        )}
      </>
    )

    return (
      <div
        className={cn(styles.container, styles[color], {
          [styles.variantCompact]: isCompact,
          [styles.variantIndicator]: isIndicator,
          [styles[`size${size}`]]: Boolean(size),
          [styles.vertical]: isVertical,
          [styles.highlightCompletedStep]: highlightCompletedStep,
        })}
      >
        {isVertical ? (
          <div className={styles.indicatorColumn}>
            {indicator}
            {trackAfter}
          </div>
        ) : (
          indicator
        )}
        {!isIndicator && (
          <div
            className={cn(styles.content, styles[status], {
              [styles.vertical]: isVertical,
            })}
          >
            <div className={styles.label}>
              <Translation text={label} span />
            </div>
            {description && (
              <div className={styles.description}>
                <Translation text={description} span />
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
)

Step.displayName = 'Step'
